"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import UsageCalculator from "@/components/pricing/UsageCalculator";
import {
  CONVERSATION_RULES,
  QUOTA_EXHAUSTED_BEHAVIOR,
  formatConversationLimit,
  UNLIMITED_CONVERSATIONS,
} from "@/lib/onboarding/plans";
import type { IPlan } from "@/types/billing";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Sparkles,
  Check,
  X,
  Crown,
  Rocket,
  Star,
  Gift,
  ShieldCheck,
  Headphones,
  Bot,
  MessageSquare,
  Globe,
  ShoppingBag,
  BarChart3,
  Users,
  Code2,
  Workflow,
  Calendar,
  Mail,
  Phone,
  HelpCircle,
  ChevronDown,
  TrendingUp,
  Layers,
  ArrowLeft,
  CircleDollarSign,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Lock,
  ServerCrash,
  Inbox,
  RefreshCw,
  Clock,
  CalendarX,
  Award,
  Zap,
} from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import PricingSkeleton from "@/components/pricing/PricingSkeleton";
import { PricingStateProvider, usePricingState } from "@/components/pricing/PricingStateContext";
import { annualPlanDiscountPercent, sharedAnnualDiscountPercent } from "@/utils/billing";

type Cycle = "monthly" | "yearly";

type PresentPlan = {
  icon: React.ElementType;
  color: string;
  accent: string;
  team: string;
  badge: string | null;
  excluded: string[];
};

const PRESENTATION: Record<string, PresentPlan> = {
  basic: {
    icon: Sparkles,
    color: "from-grayScale-500/20 to-grayScale-500/5",
    accent: "text-grayScale-700 dark:text-grayScale-200",
    team: "۱ ادمین",
    badge: null,
    excluded: ["تیم چندنفره", "API و وبهوک", "گزارش‌های پیشرفته", "A/B Testing"],
  },
  trial: {
    icon: Gift,
    color: "from-secondary-main/20 to-secondary-main/5",
    accent: "text-secondary-main",
    team: "۱ ادمین",
    badge: null,
    excluded: [],
  },
  growth: {
    icon: Rocket,
    color: "from-primary-main/30 to-primary-main/5",
    accent: "text-primary-main",
    team: "۵ نفره",
    badge: null,
    excluded: ["A/B Testing", "Sentiment Detection", "اکانت منیجر اختصاصی"],
  },
  pro: {
    icon: Crown,
    color: "from-purple/30 to-purple/5",
    accent: "text-purple",
    team: "۱۰ نفره",
    badge: null,
    excluded: ["اکانت منیجر اختصاصی ۲۴/۷"],
  },
  ultimate: {
    icon: Star,
    color: "from-secondary-main/30 to-secondary-main/5",
    accent: "text-secondary-main",
    team: "نامحدود",
    badge: "اختصاصی",
    excluded: [],
  },
};

const FALLBACK_PRESENT: PresentPlan = {
  icon: Sparkles,
  color: "from-primary-main/30 to-primary-main/5",
  accent: "text-primary-main",
  team: "—",
  badge: null,
  excluded: [],
};

const presentOf = (key: string): PresentPlan => PRESENTATION[key] ?? FALLBACK_PRESENT;

const SERVICES = [
  {
    icon: MessageSquare,
    title: "اینستاگرام و دایرکت",
    desc: "پاسخ خودکار، مدیریت کامنت، استوری و دایرکت با هوش مصنوعی فارسی.",
  },
  {
    icon: Globe,
    title: "وب‌سایت و چت لایو",
    desc: "ویجت چت آنلاین، فرم تماس هوشمند و راه‌اندازی روی هر دامنه.",
  },
  {
    icon: ShoppingBag,
    title: "فروشگاه آنلاین",
    desc: "اتصال به وردپرس، ووکامرس، شاپیفای و پلتفرم‌های ایرانی.",
  },
  {
    icon: Phone,
    title: "تلگرام و واتساپ",
    desc: "ربات اختصاصی، پاسخگوی هوشمند، CRM یکپارچه و پاسخ گروهی.",
  },
  {
    icon: Bot,
    title: "هوش مصنوعی فارسی",
    desc: "آموزش روی محتوا، لحن و دانش برند شما — فهم عامیانه و تخصصی.",
  },
  {
    icon: Calendar,
    title: "زمان‌بندی محتوا",
    desc: "تقویم محتوایی، انتشار خودکار، آنالیز بهترین زمان انتشار.",
  },
  {
    icon: BarChart3,
    title: "آنالیز و گزارش‌گیری",
    desc: "قیف فروش، نرخ تبدیل، تحلیل احساسات مشتری و KPIهای لحظه‌ای.",
  },
  {
    icon: Code2,
    title: "API و توسعه‌دهنده",
    desc: "وبهوک، API REST، SDK رسمی و مستندات کامل برای تیم فنی.",
  },
  {
    icon: Workflow,
    title: "اتوماسیون گردش‌کار",
    desc: "ساخت سناریوهای پیچیده تبدیل کاربر بدون نیاز به کد.",
  },
];

const FAQ = [
  {
    q: "دوره رایگان ۱۴ روزه چطور کار می‌کند؟",
    a: "کافی است حساب بسازید و روی «شروع رایگان ۱۴ روزه» کلیک کنید. هیچ کارتی نیاز نیست. در پایان دوره می‌توانید ادامه بدهید یا حساب غیرفعال شود.",
  },
  {
    q: "آیا می‌توانم بین پلن‌ها جابجا شوم؟",
    a: "بله، ارتقا (Upgrade) در هر زمان امکان‌پذیر است و مبلغ باقی‌مانده پلن قبلی به‌صورت Prorate محاسبه می‌شود. کاهش پلن (Downgrade) در پایان دوره فعلی اعمال می‌شود.",
  },
  {
    q: "مالیات و ارزش افزوده چطور حساب می‌شود؟",
    a: "قیمت‌های اعلام‌شده بدون مالیات هستند و ارزش افزوده ۱۰٪ در صفحه فاکتور نمایش داده می‌شود.",
  },
  {
    q: "آیا تخفیف سالانه قابل ترکیب با کد تخفیف است؟",
    a: "بله، می‌توانید کد تخفیف معتبر را روی پرداخت سالانه هم اعمال کنید؛ مگر آنکه کد محدود به یک پلن خاص باشد.",
  },
  {
    q: "اطلاعات و گفتگوها امن هستند؟",
    a: "تمام داده‌ها روی سرورهای ایران، با رمزنگاری AES-256 ذخیره می‌شوند. دسترسی‌ها بر اساس نقش، و لاگ کامل فعالیت‌ها نگهداری می‌شود.",
  },
  {
    q: "اگر نیاز به امکانات سفارشی داشته باشیم؟",
    a: "پلن «نامحدود» امکان توسعه سفارشی AI و یکپارچه‌سازی اختصاصی را با قرارداد جداگانه فراهم می‌کند. تیم فروش پاسخگو است.",
  },
];

const TESTIMONIALS = [
  {
    name: "سارا رحیمی",
    role: "مدیر برند، فروشگاه آنلاین مد",
    text: "بعد از ۲ ماه استفاده، نرخ پاسخ‌گویی دایرکت‌مون از ۴۰٪ رسید به ۹۸٪. تیم پشتیبانی هم فوق‌العاده است.",
  },
  {
    name: "محمد کاظمی",
    role: "بنیان‌گذار، آژانس دیجیتال مارکتینگ",
    text: "API و گزارش‌های پیشرو دقیقاً همون چیزی بود که برای کلاینت‌های سازمانی نیاز داشتیم.",
  },
  {
    name: "ندا موسوی",
    role: "مدیر فروش، استارتاپ EdTech",
    text: "شخصی‌سازی هوش مصنوعی روی لحن برندمون باورنکردنی دقیق کار می‌کنه.",
  },
];

function fmt(n: number) {
  return n.toLocaleString("fa-IR");
}

function PricingRoot() {
  return (
    <PricingStateProvider>
      <PricingPage />
    </PricingStateProvider>
  );
}

function PricingPage() {
  const [initialReady, setInitialReady] = useState(false);
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const state = usePricingState();

  useEffect(() => {
    const t = setTimeout(() => setInitialReady(true), 700);
    return () => clearTimeout(t);
  }, []);

  if (!initialReady || state.loading) return <PricingSkeleton />;

  return (
    <SiteShell>
      <SuccessToast message={state.successMessage} />
      <Hero />
      <StatusBanners />
      {!state.trialUsed && !state.activePlanId && !state.expired && (
        <TrialBand
          loggedIn={state.loggedIn}
          trialActive={state.trialActive}
          daysLeft={state.trialDaysLeft}
          trialName={state.plans.find((p) => p.is_trial)?.name ?? "آزمایشی ۱۴ روزه"}
        />
      )}
      <CycleSwitch cycle={cycle} setCycle={setCycle} />
      {state.errorMessage && state.isOn("error-load") ? (
        <LoadErrorState />
      ) : state.plans.length === 0 ? (
        <EmptyState />
      ) : (
        <PlansGrid cycle={cycle} />
      )}
      <UsageCalculator plans={state.plans} loggedIn={state.loggedIn} />
      <CommonRules />
      <ServicesSection />
      <CompareTable />
      <Testimonials />
      <Faq open={openFaq} setOpen={setOpenFaq} />
      <FinalCta />
    </SiteShell>
  );
}

function StatusBanners() {
  const s = usePricingState();
  return (
    <div className="mx-auto max-w-6xl space-y-3 px-6 pt-4">
      <AnimatePresence mode="popLayout">
        {s.expired && (
          <motion.div
            key="expired"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-alert-error-main/30 bg-alert-error-main/10 p-4"
          >
            <CalendarX size={18} className="text-alert-error-main" />
            <div className="flex-1 min-w-[180px]">
              <p className="text-[13px] font-black text-foreground">پلن شما منقضی شده</p>
              <p className="text-[11.5px] text-muted-foreground">
                برای ادامه دسترسی به امکانات، پلن خود را تمدید کنید.
              </p>
            </div>
            <Link
              href={
                s.activePlanId
                  ? `/dashboard/plan/checkout?plan=${s.activePlanId}&cycle=monthly`
                  : "/dashboard/pricing"
              }
              className="rounded-xl bg-alert-error-main px-4 py-2 text-[12px] font-black text-white hover:opacity-90"
            >
              تمدید فوری
            </Link>
          </motion.div>
        )}

        {s.trialActive && (
          <motion.div
            key="trial"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary-main/30 bg-primary-main/10 p-4"
          >
            <Clock size={18} className="text-primary-main" />
            <div className="flex-1 min-w-[180px]">
              <p className="text-[13px] font-black text-foreground">
                ترایال شما فعال است — {fmt(s.trialDaysLeft)} روز باقی‌مانده
              </p>
              <p className="text-[11.5px] text-muted-foreground">
                برای استفاده بدون وقفه، یکی از پلن‌های زیر را قبل از پایان ترایال انتخاب کنید.
              </p>
            </div>
          </motion.div>
        )}

        {s.activePlanId && !s.expired && !s.trialActive && (
          <motion.div
            key="active"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary-main/30 bg-card p-4"
          >
            <Award size={18} className="text-primary-main" />
            <div className="flex-1 min-w-[180px]">
              <p className="text-[13px] font-black text-foreground">
                پلن فعلی شما: {s.plans.find((p) => p.key === s.activePlanId)?.name ?? "فعال"}
              </p>
              <p className="text-[11.5px] text-muted-foreground">
                برای ارتقا، یکی از پلن‌های بالاتر را انتخاب کنید — مبلغ Prorate خودکار محاسبه
                می‌شود.
              </p>
            </div>
          </motion.div>
        )}

        {s.errorMessage && !s.isOn("error-load") && (
          <motion.div
            key="err"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-3 rounded-2xl border border-alert-error-main/30 bg-alert-error-main/10 p-4"
          >
            {s.isOn("error-401") ? (
              <Lock size={18} className="text-alert-error-main" />
            ) : s.isOn("error-422") ? (
              <AlertTriangle size={18} className="text-alert-error-main" />
            ) : (
              <ServerCrash size={18} className="text-alert-error-main" />
            )}
            <p className="text-[13px] font-bold text-foreground">{s.errorMessage}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SuccessToast({ message }: { message: string | null }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.95 }}
          className="fixed left-1/2 top-20 z-[120] flex -translate-x-1/2 items-center gap-2.5 rounded-2xl border border-primary-main/30 bg-primary-main px-5 py-3 text-white shadow-2xl shadow-primary-main/40"
        >
          <CheckCircle2 size={18} />
          <span className="text-[13px] font-black">{message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Hero() {
  return (
    <section className="relative mx-auto max-w-6xl px-6 pt-16 pb-10 sm:pt-24">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <CircleDollarSign size={14} className="text-primary-main" />
          <span className="text-[12px] font-bold text-muted-foreground">
            قیمت‌گذاری شفاف، بدون هزینه پنهان
          </span>
        </div>
        <h1 className="mb-5 text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
          پلنی متناسب با
          <span className="block brand-gradient-text">مقیاس کسب‌وکار شما</span>
        </h1>
        <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          از مدیریت دایرکت اینستاگرام تا API سازمانی — همه چیز در یک پلتفرم. ۱۴ روز رایگان امتحان
          کنید، خدمات فراتر از تصور.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <TrustChip icon={ShieldCheck} text="پرداخت امن" />
          <TrustChip icon={Headphones} text="پشتیبانی فارسی ۲۴/۷" />
          <TrustChip icon={RefreshCw} text="ضمانت بازگشت ۷ روزه" />
          <TrustChip icon={Zap} text="فعال‌سازی فوری" />
        </div>
      </motion.div>
    </section>
  );
}

function TrustChip({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[11.5px] font-bold text-muted-foreground">
      <Icon size={12} className="text-primary-main" /> {text}
    </span>
  );
}

function TrialBand({
  loggedIn,
  trialActive,
  daysLeft,
  trialName,
}: {
  loggedIn: boolean;
  trialActive: boolean;
  daysLeft: number;
  trialName: string;
}) {
  if (trialActive) return null;
  return (
    <section className="mx-auto max-w-6xl px-6 pt-6 pb-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative overflow-hidden rounded-3xl border border-primary-main/30 bg-gradient-to-l from-primary-main/15 via-primary-main/5 to-secondary-main/10 p-6 sm:p-10"
      >
        <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-primary-main/30 blur-3xl" />
        <div className="absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-secondary-main/30 blur-3xl" />
        <div className="relative grid items-center gap-6 sm:grid-cols-[1fr_auto]">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-background/70 px-3 py-1.5 backdrop-blur">
              <Gift size={13} className="text-secondary-main" />
              <span className="text-[11.5px] font-black text-foreground">پیشنهاد ویژه</span>
            </div>
            <h2 className="mb-2 text-2xl font-black text-foreground sm:text-3xl">
              ۱۴ روز کامل رایگان امتحان کنید
            </h2>
            <p className="max-w-xl text-[13.5px] leading-relaxed text-muted-foreground">
              همه امکانات پلن «{trialName}» — بدون کارت بانکی، بدون تعهد. هر زمان خواستید لغو کنید.
            </p>
          </div>
          <Link
            href={loggedIn ? "/dashboard" : "/login"}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary-main px-6 py-3.5 text-[13.5px] font-black text-white shadow-lg shadow-primary-main/40 transition-transform hover:scale-[1.02]"
          >
            <Sparkles size={15} />
            شروع رایگان ۱۴ روزه
          </Link>
        </div>
      </motion.div>
    </section>
  );
}

function CycleSwitch({ cycle, setCycle }: { cycle: Cycle; setCycle: (c: Cycle) => void }) {
  const s = usePricingState();
  const annualDiscount = sharedAnnualDiscountPercent(s.plans);
  return (
    <section className="mx-auto max-w-6xl px-6 pt-4 pb-6">
      <div className="flex flex-wrap items-center justify-center gap-3">
        <div className="relative inline-flex rounded-2xl border border-border bg-card p-1">
          {(["monthly", "yearly"] as Cycle[]).map((c) => (
            <button
              key={c}
              onClick={() => setCycle(c)}
              className={`relative z-10 rounded-xl px-5 py-2.5 text-[12.5px] font-black transition-colors ${
                cycle === c ? "text-white" : "text-muted-foreground"
              }`}
            >
              {cycle === c && (
                <motion.span
                  layoutId="cycle-pill"
                  className="absolute inset-0 -z-10 rounded-xl bg-primary-main"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}
              {c === "monthly" ? "ماهانه" : "سالانه"}
            </button>
          ))}
        </div>
        {annualDiscount !== null && (
          <span className="rounded-full border border-secondary-main/30 bg-secondary-main/10 px-3 py-1 text-[11px] font-black text-secondary-main">
            {fmt(annualDiscount)}٪ تخفیف سالانه
          </span>
        )}
      </div>
    </section>
  );
}

function PlansGrid({ cycle }: { cycle: Cycle }) {
  const s = usePricingState();
  const visible = s.plans;
  const currentIdx = s.activePlanId ? visible.findIndex((x) => x.key === s.activePlanId) : -1;
  return (
    <section className="mx-auto max-w-7xl px-6 pb-16">
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
        {visible.map((p, i) => (
          <PlanCard
            key={p.key}
            plan={p}
            present={presentOf(p.key)}
            cycle={cycle}
            delay={i * 0.06}
            isCurrent={s.activePlanId === p.key}
            isUpgrade={currentIdx !== -1 && i > currentIdx}
            isDowngrade={currentIdx !== -1 && i < currentIdx}
            isSelecting={s.selectingPlanId === p.key}
            expired={s.expired}
            loggedIn={s.loggedIn}
            canStartTrial={p.is_trial && s.loggedIn && !s.activePlanId}
          />
        ))}
      </div>

      <div className="mt-8 rounded-3xl border border-primary-main/20 bg-primary-main/5 p-5 sm:p-6">
        <h3 className="text-base font-black text-grayScale-900 dark:text-white">
          «مکالمه» دقیقاً یعنی چه؟
        </h3>
        <p className="mt-1 text-[13px] text-grayScale-600 dark:text-grayScale-300">
          مکالمه = یک پنجرهٔ ۲۴ ساعته با یک مخاطب. هر تعداد پیام در این بازه، فقط یک مکالمه حساب
          می‌شود.
        </p>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          {CONVERSATION_RULES.map((r) => (
            <li
              key={r}
              className="flex items-start gap-2 text-[12.5px] text-grayScale-700 dark:text-grayScale-200"
            >
              <Check size={14} className="mt-0.5 flex-shrink-0 text-alert-success-main" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 rounded-2xl bg-amber/10 px-3 py-2 text-[12px] font-bold text-amber">
          {QUOTA_EXHAUSTED_BEHAVIOR}
        </p>
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  present,
  cycle,
  delay,
  isCurrent,
  isUpgrade,
  isDowngrade,
  isSelecting,
  canStartTrial,
  expired,
  loggedIn,
}: {
  plan: IPlan;
  present: PresentPlan;
  cycle: Cycle;
  delay: number;
  isCurrent: boolean;
  isUpgrade: boolean;
  isDowngrade: boolean;
  isSelecting: boolean;
  canStartTrial: boolean;
  expired: boolean;
  loggedIn: boolean;
}) {
  const Icon = present.icon;
  const isTrial = plan.is_trial;
  const monthly = plan.monthly;
  const yearly = plan.yearly;
  const annualPct = annualPlanDiscountPercent(plan);
  const displayPrice = cycle === "monthly" ? monthly : Math.round(yearly / 12);
  const strikePrice = cycle === "monthly" ? plan.compare_monthly : plan.compare_yearly;
  const showStrike = strikePrice !== null && strikePrice > displayPrice;
  const isHighlight = plan.is_recommended;
  const unlimited = plan.conversation_limit >= UNLIMITED_CONVERSATIONS;
  const badge = present.badge ?? (isHighlight ? "پیشنهادی" : null);
  const checkoutHref = `/dashboard/plan/checkout?plan=${plan.key}&cycle=${cycle}`;

  const { trialBusy, activateTrial } = usePricingState();
  let ctaLabel = isTrial ? "شروع رایگان ۱۴ روزه" : `انتخاب پلن ${plan.name}`;
  if (isCurrent && !expired) ctaLabel = "پلن فعلی شما";
  else if (isTrial && expired) ctaLabel = "پایان دورهٔ آزمایشی";
  else if (isCurrent && expired) ctaLabel = "تمدید پلن";
  else if (isUpgrade) ctaLabel = `ارتقا به ${plan.name}`;
  else if (isDowngrade) ctaLabel = "کاهش پلن (در پایان دوره)";

  const ctaDisabled =
    (isCurrent && !expired) || isSelecting || isDowngrade || (isTrial && expired);

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay }}
      className={`relative flex flex-col rounded-3xl border bg-card p-6 transition-all hover:shadow-xl ${
        isCurrent
          ? "border-primary-main/60 shadow-lg shadow-primary-main/20 ring-2 ring-primary-main/40"
          : isHighlight
            ? "border-primary-main/50 shadow-lg shadow-primary-main/10 ring-1 ring-primary-main/30"
            : "border-border"
      }`}
    >
      {isCurrent ? (
        <span className="absolute -top-3 right-6 inline-flex items-center gap-1 rounded-full bg-primary-main px-3 py-1 text-[10.5px] font-black text-white">
          <Award size={11} /> پلن فعلی شما
        </span>
      ) : badge ? (
        <span
          className={`absolute -top-3 right-6 rounded-full px-3 py-1 text-[10.5px] font-black ${
            isHighlight ? "bg-primary-main text-white" : "bg-secondary-main text-white"
          }`}
        >
          {badge}
        </span>
      ) : null}

      <div
        className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${present.color}`}
      >
        <Icon size={20} className={present.accent} />
      </div>

      <h3 className="text-xl font-black text-foreground">{plan.name}</h3>
      <p className="mt-1 text-[12.5px] text-muted-foreground">{plan.tagline}</p>

      <div className="mt-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={cycle}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            {isTrial ? (
              <div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-black text-foreground">رایگان</span>
                  <span className="text-[12px] font-bold text-muted-foreground">۱۴ روز کامل</span>
                </div>
                <div className="mt-1 text-[11.5px] font-bold text-secondary-main">
                  بدون نیاز به کارت بانکی
                </div>
              </div>
            ) : (
              <>
                {showStrike && strikePrice !== null && (
                  <div className="text-[12px] text-muted-foreground line-through">
                    {fmt(strikePrice)} تومان
                  </div>
                )}
                {plan.campaign_active && (
                  <div className="mt-0.5 inline-flex rounded-full bg-secondary-main/10 px-2 py-0.5 text-[10px] font-black text-secondary-main">
                    {plan.campaign_label ?? `${fmt(plan.discount_percent)}٪ تخفیف`}
                  </div>
                )}
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-black text-foreground">{fmt(displayPrice)}</span>
                  <span className="text-[12px] font-bold text-muted-foreground">تومان / ماه</span>
                </div>
                {cycle === "yearly" && (
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] font-bold text-primary-main">
                    <span>مبلغ سالانه: {fmt(yearly)} تومان</span>
                    {annualPct > 0 && (
                      <span className="rounded-full bg-secondary-main/10 px-2 py-0.5 text-[10px] font-black text-secondary-main">
                        {fmt(annualPct)}٪ تخفیف سالانه
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {isUpgrade && (
        <div className="mt-3 rounded-xl border border-primary-main/30 bg-primary-main/5 p-2.5 text-[11px] leading-relaxed text-foreground">
          <span className="font-black text-primary-main">Prorate:</span> اعتبار باقی‌مانده پلن فعلی
          به‌صورت خودکار از این مبلغ کسر می‌شود.
        </div>
      )}

      <div className="my-5 grid grid-cols-2 gap-2 rounded-2xl border border-border bg-muted/40 p-3 text-center">
        <Stat
          label="مکالمه هوشمند"
          value={
            isTrial
              ? `${formatConversationLimit(plan.conversation_limit)} در کل دوره`
              : `${formatConversationLimit(plan.conversation_limit)} / ماه`
          }
          icon={Bot}
        />
        <Stat label="تیم" value={present.team} icon={Users} />
      </div>

      <ul className="mb-6 space-y-2.5">
        {plan.features.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2 text-[12.5px] leading-relaxed text-foreground"
          >
            <Check size={14} className="mt-0.5 shrink-0 text-primary-main" />
            <span>{f}</span>
          </li>
        ))}
        {present.excluded.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2 text-[12.5px] leading-relaxed text-muted-foreground/70"
          >
            <X size={14} className="mt-0.5 shrink-0 text-muted-foreground/60" />
            <span className="line-through">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto space-y-2">
        {isSelecting ? (
          <button
            disabled
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-main px-4 py-3 text-[13px] font-black text-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            <Loader2 size={14} className="animate-spin" /> در حال پردازش…
          </button>
        ) : canStartTrial ? (
          <button
            onClick={() => void activateTrial()}
            disabled={trialBusy}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-main px-4 py-3 text-[13px] font-black text-white transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {trialBusy ? (
              <>
                <Loader2 size={14} className="animate-spin" /> در حال فعال‌سازی…
              </>
            ) : (
              <>
                {ctaLabel}
                <ArrowLeft size={14} />
              </>
            )}
          </button>
        ) : ctaDisabled ? (
          <button
            disabled
            className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-[13px] font-black disabled:cursor-not-allowed ${
              isCurrent && !expired
                ? "bg-primary-main/15 text-primary-main"
                : "bg-foreground text-background opacity-50"
            }`}
          >
            {isCurrent && !expired ? <CheckCircle2 size={14} /> : null}
            {ctaLabel}
          </button>
        ) : (
          <Link
            href={
              isTrial
                ? "/login"
                : loggedIn
                  ? checkoutHref
                  : `/login?next=${encodeURIComponent(checkoutHref)}`
            }
            className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-[13px] font-black transition-colors ${
              isHighlight
                ? "bg-primary-main text-white hover:bg-primary-400"
                : "bg-foreground text-background hover:opacity-90"
            }`}
          >
            {ctaLabel}
            <ArrowLeft size={14} />
          </Link>
        )}
        <p className="text-center text-[10.5px] text-muted-foreground">
          {isTrial
            ? "بدون کارت بانکی، بدون تعهد"
            : unlimited
              ? "مکالمهٔ هوشمند نامحدود"
              : `هر مکالمهٔ اضافه: ${fmt(plan.per_conversation_price)} تومان`}
        </p>
      </div>
    </motion.div>
  );
}

function Stat({ label, value, icon: Icon }: { label: string; value: string; icon: typeof Bot }) {
  return (
    <div>
      <div className="mb-1 inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
        <Icon size={11} /> {label}
      </div>
      <div className="text-[12.5px] font-black text-foreground">{value}</div>
    </div>
  );
}

function LoadErrorState() {
  const s = usePricingState();
  return (
    <section className="mx-auto max-w-3xl px-6 pb-16">
      <div className="rounded-3xl border border-alert-error-main/30 bg-alert-error-main/5 p-10 text-center">
        <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-alert-error-main/15">
          <ServerCrash size={22} className="text-alert-error-main" />
        </div>
        <h3 className="text-xl font-black text-foreground">بارگذاری پلن‌ها با خطا مواجه شد</h3>
        <p className="mx-auto mt-2 max-w-md text-[13px] text-muted-foreground">
          لطفاً اتصال خود را بررسی کنید و دوباره تلاش کنید.
        </p>
        <button
          onClick={s.reload}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-alert-error-main px-5 py-2.5 text-[12.5px] font-black text-white hover:opacity-90"
        >
          <RefreshCw size={14} /> تلاش مجدد
        </button>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="mx-auto max-w-3xl px-6 pb-16">
      <div className="rounded-3xl border border-border bg-card p-10 text-center">
        <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
          <Inbox size={22} className="text-muted-foreground" />
        </div>
        <h3 className="text-xl font-black text-foreground">در حال حاضر پلنی فعال نیست</h3>
        <p className="mx-auto mt-2 max-w-md text-[13px] text-muted-foreground">
          تیم ما در حال به‌روزرسانی پلن‌هاست. به‌زودی برمی‌گردیم — برای اطلاع از انتشار، با ما در
          تماس باشید.
        </p>
        <Link
          href="/contact"
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary-main px-5 py-2.5 text-[12.5px] font-black text-white hover:bg-primary-400"
        >
          <Mail size={14} /> اطلاع به من
        </Link>
      </div>
    </section>
  );
}

function CommonRules() {
  const items = [
    { icon: ShieldCheck, t: "پرداخت امن", d: "درگاه‌های معتبر بانکی + رمزنگاری کامل" },
    { icon: TrendingUp, t: "Prorate ارتقا", d: "اعتبار باقی‌مانده پلن قبلی محاسبه می‌شود" },
    { icon: Headphones, t: "پشتیبانی فارسی", d: "تیم پشتیبانی ۲۴/۷ به زبان فارسی" },
    { icon: Layers, t: "بدون تعهد", d: "هر زمان لغو کنید — بدون جریمه" },
  ];
  return (
    <section className="mx-auto max-w-6xl px-6 pb-16">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.t} className="rounded-2xl border border-border bg-card p-5">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-main/10">
                <Icon size={16} className="text-primary-main" />
              </div>
              <h4 className="text-[13px] font-black text-foreground">{it.t}</h4>
              <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">{it.d}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="mx-auto max-w-6xl px-6 pb-20">
      <div className="mb-10 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <Layers size={13} className="text-secondary-main" />
          <span className="text-[12px] font-bold text-muted-foreground">آنچه شامل می‌شود</span>
        </div>
        <h2 className="text-3xl font-black text-foreground sm:text-4xl">خدمات کامل در یک پلتفرم</h2>
        <p className="mx-auto mt-3 max-w-xl text-[13.5px] text-muted-foreground">
          ترندی اسمارت فقط دایرکت اینستاگرام نیست — یک اکوسیستم کامل برای رشد دیجیتال است.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary-main/40 hover:shadow-lg"
            >
              <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary-main/15 to-secondary-main/10 transition-transform group-hover:scale-110">
                <Icon size={18} className="text-primary-main" />
              </div>
              <h3 className="text-[14px] font-black text-foreground">{s.title}</h3>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">{s.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function buildFeatureMatrix(plans: IPlan[]): { label: string; values: boolean[] }[] {
  const seen = new Set<string>();
  const rows: { label: string; values: boolean[] }[] = [];
  for (const plan of plans) {
    for (const feature of plan.features) {
      if (seen.has(feature)) continue;
      seen.add(feature);
      rows.push({ label: feature, values: plans.map((p) => p.features.includes(feature)) });
    }
  }
  return rows;
}

function CompareTable() {
  const s = usePricingState();
  const plans = s.plans;
  if (plans.length === 0) return null;
  const catalogRows = [
    {
      label: "مکالمهٔ هوشمند",
      values: plans.map((p) =>
        p.is_trial
          ? `${formatConversationLimit(p.conversation_limit)} در کل دوره`
          : `${formatConversationLimit(p.conversation_limit)} در ماه`,
      ),
    },
    {
      label: "قیمت هر مکالمهٔ اضافه",
      values: plans.map((p) =>
        p.is_trial || p.conversation_limit >= UNLIMITED_CONVERSATIONS
          ? "—"
          : `${fmt(p.per_conversation_price)} ت`,
      ),
    },
    {
      label: "کانال‌های متصل",
      values: plans.map((p) =>
        p.channel_limit >= UNLIMITED_CONVERSATIONS ? "نامحدود" : `${fmt(p.channel_limit)} کانال`,
      ),
    },
  ];
  const rows = [...catalogRows, ...buildFeatureMatrix(plans)];
  return (
    <section className="mx-auto max-w-7xl px-6 pb-20">
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-black text-foreground sm:text-4xl">مقایسه دقیق پلن‌ها</h2>
        <p className="mx-auto mt-3 max-w-xl text-[13.5px] text-muted-foreground">
          بهترین تصمیم را با مقایسه ویژگی‌های هر پلن بگیرید.
        </p>
      </div>

      <div className="overflow-x-auto rounded-3xl border border-border bg-card">
        <table className="w-full min-w-[760px] text-right">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="p-4 text-[12px] font-black text-muted-foreground">ویژگی</th>
              {plans.map((p) => (
                <th key={p.key} className="p-4 text-center">
                  <div className={`text-[13px] font-black ${presentOf(p.key).accent}`}>
                    {p.name}
                  </div>
                  <div className="mt-1 text-[10.5px] font-bold text-muted-foreground">
                    {p.is_trial ? "رایگان" : `${fmt(p.monthly)} ت / ماه`}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.label} className={idx % 2 ? "bg-muted/20" : ""}>
                <td className="p-3.5 text-[12.5px] font-bold text-foreground">{row.label}</td>
                {row.values.map((v, i) => (
                  <td key={i} className="p-3.5 text-center text-[12.5px]">
                    {typeof v === "boolean" ? (
                      v ? (
                        <Check size={16} className="mx-auto text-primary-main" />
                      ) : (
                        <X size={16} className="mx-auto text-muted-foreground/50" />
                      )
                    ) : (
                      <span className="font-bold text-foreground">{v}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="mx-auto max-w-6xl px-6 pb-20">
      <div className="mb-10 text-center">
        <h2 className="text-3xl font-black text-foreground sm:text-4xl">
          مشتری‌های ما چه می‌گویند
        </h2>
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <motion.blockquote
            key={t.name}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.07 }}
            className="rounded-2xl border border-border bg-card p-6"
          >
            <div className="mb-3 flex gap-0.5">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} size={13} className="fill-secondary-main text-secondary-main" />
              ))}
            </div>
            <p className="text-[13px] leading-relaxed text-foreground">«{t.text}»</p>
            <footer className="mt-4 border-t border-border pt-3">
              <div className="text-[12.5px] font-black text-foreground">{t.name}</div>
              <div className="text-[11px] text-muted-foreground">{t.role}</div>
            </footer>
          </motion.blockquote>
        ))}
      </div>
    </section>
  );
}

function Faq({ open, setOpen }: { open: number | null; setOpen: (n: number | null) => void }) {
  return (
    <section className="mx-auto max-w-3xl px-6 pb-20">
      <div className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <HelpCircle size={13} className="text-primary-main" />
          <span className="text-[12px] font-bold text-muted-foreground">سؤالات متداول</span>
        </div>
        <h2 className="text-3xl font-black text-foreground sm:text-4xl">پاسخ سریع، شفاف</h2>
      </div>
      <div className="space-y-3">
        {FAQ.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.q} className="overflow-hidden rounded-2xl border border-border bg-card">
              <button
                onClick={() => setOpen(isOpen ? null : i)}
                className="flex w-full items-center justify-between gap-3 p-5 text-right"
              >
                <span className="text-[13.5px] font-black text-foreground">{f.q}</span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p className="border-t border-border p-5 text-[12.5px] leading-relaxed text-muted-foreground">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FinalCta() {
  const { loggedIn, trialActive, activePlanId, trialBusy, activateTrial } =
    usePricingState();
  const canStartTrial = loggedIn && !activePlanId;
  const hasPlan = loggedIn && activePlanId !== null;
  const ctaClasses =
    "inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-background px-6 py-3.5 text-[13px] font-black text-foreground hover:bg-muted";

  return (
    <section className="mx-auto max-w-6xl px-6 pb-24">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary-main/15 via-card to-secondary-main/10 p-8 text-center sm:p-14">
        <div className="absolute inset-0 -z-0 opacity-40">
          <div className="absolute -left-20 top-0 h-60 w-60 rounded-full bg-primary-main/30 blur-3xl" />
          <div className="absolute -right-20 bottom-0 h-60 w-60 rounded-full bg-secondary-main/30 blur-3xl" />
        </div>
        <div className="relative">
          <h2 className="text-3xl font-black text-foreground sm:text-4xl">
            هنوز مطمئن نیستید کدام پلن مناسب شماست؟
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-[13.5px] text-muted-foreground">
            با تیم فروش ما صحبت کنید — یک دموی اختصاصی برای کسب‌وکار شما برگزار می‌کنیم.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              href="/contact"
              target="_blank"
  rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl bg-primary-main px-6 py-3.5 text-[13px] font-black text-white shadow-lg shadow-primary-main/30 hover:bg-primary-400"
            >
              <Mail size={15} /> تماس با تیم فروش
            </Link>
            {canStartTrial ? (
              <button
                onClick={() => void activateTrial()}
                disabled={trialBusy}
                className={`${ctaClasses} disabled:cursor-not-allowed disabled:opacity-70`}
              >
                {trialBusy ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> در حال فعال‌سازی…
                  </>
                ) : (
                  <>
                    <Sparkles size={15} /> شروع رایگان ۱۴ روزه
                  </>
                )}
              </button>
            ) : trialActive ? (
              <span className="inline-flex items-center gap-2 rounded-2xl border border-primary-main/30 bg-primary-main/10 px-6 py-3.5 text-[13px] font-black text-primary-main">
                <CheckCircle2 size={15} /> پلن فعلی شما
              </span>
            ) : hasPlan ? (
              <Link href="/dashboard/pricing" className={ctaClasses}>
                <Crown size={15} /> مشاهده همه پلن‌ها
              </Link>
            ) : (
              <Link href="/auth" className={ctaClasses}>
                <Sparkles size={15} /> شروع رایگان
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default PricingRoot;
